import type { JSX } from 'react';
import { useState } from 'react';
import { ChevronsUpDown, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCandidateFilter, type SelectableCandidate } from '../../hooks/useCandidateFilter';
import { CandidateSelectorDropdown } from './CandidateSelectorDropdown';

interface CandidateSelectorProps {
  candidates: SelectableCandidate[];
  selectedCandidateId: string | null;
  onSelect: (candidateId: string | null) => void;
  excludeIds?: string[];
  isLoading?: boolean;
  placeholder?: string;
}

interface TriggerLabelProps {
  isLoading: boolean;
  selectedCandidate?: SelectableCandidate;
  placeholder: string;
}

function TriggerLabel({
  isLoading,
  selectedCandidate,
  placeholder,
}: TriggerLabelProps): JSX.Element {
  if (isLoading) return <>Loading...</>;
  if (selectedCandidate) {
    return (
      <span className="flex items-center gap-2">
        <User className="h-4 w-4" />
        {selectedCandidate.name}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2 text-muted-foreground">
      <Search className="h-4 w-4" />
      {placeholder}
    </span>
  );
}

export function CandidateSelector({
  candidates,
  selectedCandidateId,
  onSelect,
  excludeIds = [],
  isLoading = false,
  placeholder = 'Select candidate...',
}: CandidateSelectorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { searchValue, setSearchValue, available, filtered } = useCandidateFilter(
    candidates,
    excludeIds
  );
  const selectedCandidate = candidates.find((c) => c.id === selectedCandidateId);

  const handleSelect = (value: string): void => {
    onSelect(value === selectedCandidateId ? null : value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={isLoading}
        >
          <TriggerLabel
            isLoading={isLoading}
            selectedCandidate={selectedCandidate}
            placeholder={placeholder}
          />
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <CandidateSelectorDropdown
          filtered={filtered}
          availableCount={available.length}
          selectedCandidateId={selectedCandidateId}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
}
